import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from "@angular/core";
import { ReadTaskActionDTO, Task, TaskAssignee, TaskListItem } from "../taskbook-triage/taskbook.types";
import { DateRange } from "@angular/material/datepicker";
import { FormatedSavedFilter } from "@teammood/filter";
import { SavedFilterChange } from "../filter/saved-filter/saved-filter.component";
import { TaskbookService } from "../taskbook-triage/taskbook.service";
import { filter, mergeMap, of, Subject, takeUntil, tap } from "rxjs";
import { User, UserService } from "@teammood/shared/util/user";
import { PollUnderCreation } from "../polls/create-poll/create-poll.type";
import { CreatePollService } from "../polls/create-poll/create-poll.service";
import { TimelineItemDialogService } from "@teammood/timeline";
import { SnackBarService } from "@teammood/shared/ui/snack-bar";
import { MoodsService } from "../timeline/timeline-item/moods/moods.service";
import { LoggerService } from "@teammood/shared/util/logger";
import { TranslateService } from "@ngx-translate/core";
import { DaysService } from "../timeline/daily-overview/days.service";
import { ITeam } from "@teammood/team";
import { Router } from "@angular/router";

@Component({
    selector: 'taskbook-backlog',
    templateUrl: './taskbook-backlog.component.html',
    styleUrls: ['./taskbook-backlog.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TaskbookBacklogComponent implements OnInit, OnDestroy {

    protected tasks: TaskListItem[] = [];
    protected user: User;
    protected teams: ITeam[];
    protected managers: TaskAssignee[];

    protected loading = true;

    protected counters: {
        inProgress: number;
        toReview: number;
    } = {
        inProgress: 0,
        toReview: 0,
    }

    pageTitle = '';
    dateInterval: DateRange<Date>;
    selectedFilter: FormatedSavedFilter;

    private _destroy$ = new Subject<void>();

    constructor(
        private taskbookService: TaskbookService,
        private userService: UserService,
        private timelineItemDialogService: TimelineItemDialogService,
        private createPollService: CreatePollService,
        private moodsService: MoodsService,
        private daysService: DaysService,
        private snackBarService: SnackBarService,
        private logger: LoggerService,
        private translateService: TranslateService,
        private router: Router,
    ){}

    ngOnInit(): void {
        this.userService.get().then(async (user) => {
            if (user) {
                this.user = user;
                this.teams = [...user.teams];
            }
        })
    }

    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }

    loadTaskbook(filter: SavedFilterChange = null){
        this.taskbookService.tasks(filter).pipe(
            tap(async (taskbook: {managers: TaskAssignee[], taskList: TaskListItem[]}) => {
                const inProgress = taskbook.taskList.filter(task => task.isRead(this.user.id)).length;
                this.counters = {
                    inProgress: inProgress,
                    toReview: taskbook.taskList.length - inProgress,
                }
                this.tasks = taskbook.taskList;
                this.managers = taskbook.managers
                this.loading = false;
            }),
            takeUntil(this._destroy$)
        ).subscribe();
    }

    onFilterChange(filter: SavedFilterChange){
        this.selectedFilter = filter.filter;
        this.loadTaskbook(filter)
    }

    openTaskDetails(item: TaskListItem){
        of(this.router.navigate(['/backlog', item.task.itemType, item.task.id], {
            queryParams: {filter: this.selectedFilter.filter},
        })).pipe(
            mergeMap( () => {
                const readTaskAction = new ReadTaskActionDTO(item.task.id, item.task.itemType, this.selectedFilter.decodedFilter);
                return this.taskbookService.saveAction([readTaskAction]);
            }),
        ).subscribe();
    }


    protected async poll(task?: Task){
        const pollDialogRef = await this.timelineItemDialogService.createPoll(this.teams, [], '');
        pollDialogRef.beforeClosed().pipe(
            filter((poll) => !!poll),
            mergeMap((poll: PollUnderCreation) => {
                return this.addPoll(poll);
            }),
            takeUntil(pollDialogRef.afterClosed())
        ).subscribe();
    }
    private async addPoll(question: any) {
        await this.createPollService.createPoll(question);
        if(question._serializePoll){
            this.poll(null);
            this.snackBarService.success(this.translateService.instant('question_created'));
        }else{
            const sucessDialog = await this.timelineItemDialogService.questionSuccess();
            sucessDialog.beforeClosed().pipe().subscribe();
        }
    }
    protected async kudos(task?: Task) {
        const dialogRef = await this.timelineItemDialogService.createKudos(this.teams, this.user);
        dialogRef.beforeClosed().pipe(
            filter((kudos) => !!kudos),
            mergeMap(kudos => {
                if (kudos.message?.body) {
                    kudos.message.body = this.moodsService.addSpaceBeforeLink(kudos.message.body);
                    return this.daysService.createKudos(kudos, kudos.teamId).then(async () => {
                        this.snackBarService.success(this.translateService.instant('kudos_created'));
                    }).catch((error) => {
                        this.logger.error('Error creating kudos:', error);
                        this.snackBarService.error(error);
                    });
                }
            }),
            takeUntil(dialogRef.afterClosed())
        ).subscribe();
    }

    protected onArchived(item: TaskListItem) {
        this.tasks = this.tasks.filter(task => task.task.id !== item.task.id);
        if (item.isRead(this.user.id)) {
            this.counters = {
                inProgress: this.counters.inProgress - 1 > 0?this.counters.inProgress - 1:0,
                toReview: this.counters.toReview,
            }
        } else {
            this.counters = {
                inProgress: this.counters.inProgress - 1 > 0?this.counters.inProgress - 1:0,
                toReview: this.counters.toReview > 0 ?this.counters.toReview - 1: 0,
            }
        }

    }
}

function contains(firstname: any, search: string): boolean {
    throw new Error("Function not implemented.");
}
